import type { UserPerformanceRow } from '../data/reports';
import type { ApiResult } from './types';
import { reportsApiRequest } from './apiClient';
import {
  downloadReportBlob,
  exportReportBlob,
  extractReportLookupRows,
  type ReportExportFile,
  mapReportSearchPage,
  nullIfEmpty,
  toCount,
  toLabel,
  withReportDateRange,
  withReportTimeZone,
  type ReportSearchPage,
} from './reportApiCommon';

export type UserPerformanceReportUser = {
  id: string;
  displayName: string;
};

export type UserPerformanceReportDivision = {
  id: number;
  name: string;
};

export type UserPerformanceExportFormat = 'PDF' | 'EXCEL';

/** Role codes excluded from User Performance Report filters (per data dictionary). */
export const USER_PERFORMANCE_EXCLUDED_ROLE_CODES = new Set(['DOCUMENT_MOVEMENT_OFFICER']);

export function isUserPerformanceReportRole(role: { roleCode?: string }): boolean {
  const code = (role.roleCode ?? '').trim().toUpperCase();
  return !USER_PERFORMANCE_EXCLUDED_ROLE_CODES.has(code);
}

export type UserPerformanceReportFilters = {
  roleIds: number[];
  userIds: string[];
  categoryIds: string[];
  subCategoryIds: string[];
  divisionIds: number[];
  dateFrom: string;
  dateTo: string;
};

/** @deprecated Use UserPerformanceReportFilters */
export type UserPerformanceExportFilters = UserPerformanceReportFilters;

export type UserPerformanceSearchResult = ReportSearchPage<UserPerformanceRow>;

type ApiUserRow = {
  id?: string;
  user_id?: string;
  display_name?: string;
  displayName?: string;
  user_name?: string;
};

type ApiDivisionRow = {
  id?: number;
  name?: string;
  display_name?: string;
  displayName?: string;
};

type ApiSearchRow = {
  user_id?: string;
  id?: string;
  user_name?: string;
  display_name?: string;
  user_roles?: string | string[] | null;
  role_names?: string | string[] | null;
  roles?: string | string[] | null;
  division_name?: string;
  division?: string;
  assigned?: number | string | null;
  assigned_count?: number | string | null;
  allocated?: number | string | null;
  allocated_count?: number | string | null;
  escalated?: number | string | null;
  escalated_count?: number | string | null;
  completed?: number | string | null;
  completed_count?: number | string | null;
};

function mapUserRow(row: ApiUserRow): UserPerformanceReportUser | null {
  const id = (row.id ?? row.user_id ?? '').trim();
  const displayName = (row.display_name ?? row.displayName ?? row.user_name ?? '').trim();
  if (!id || !displayName) return null;
  return { id, displayName };
}

function mapDivisionRow(row: ApiDivisionRow): UserPerformanceReportDivision | null {
  const id = row.id;
  const name = (row.display_name ?? row.displayName ?? row.name ?? '').trim();
  if (typeof id !== 'number' || !Number.isFinite(id) || !name) return null;
  return { id, name };
}

/** Build GET /user-performance/users query (`role_ids=1&role_ids=2`). Role ids are optional. */
export function buildUserPerformanceUsersQuery(roleIds: number[] = []): string {
  const qs = new URLSearchParams();
  for (const id of roleIds) {
    if (Number.isFinite(id)) qs.append('role_ids', String(id));
  }
  const query = qs.toString();
  return query ? `?${query}` : '';
}

export async function listUserPerformanceReportUsers(
  roleIds: number[] = [],
  signal?: AbortSignal,
): Promise<ApiResult<UserPerformanceReportUser[]>> {
  const res = await reportsApiRequest<unknown>(`/user-performance/users${buildUserPerformanceUsersQuery(roleIds)}`, {
    signal,
  });
  if (!res.ok) return { success: false, message: res.message };

  const mapped = extractReportLookupRows<ApiUserRow>(res.data)
    .map(mapUserRow)
    .filter((row): row is UserPerformanceReportUser => row !== null)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { success: true, data: mapped };
}

export async function listUserPerformanceReportDivisions(): Promise<ApiResult<UserPerformanceReportDivision[]>> {
  const res = await reportsApiRequest<unknown>('/user-performance/divisions');
  if (!res.ok) return { success: false, message: res.message };

  const mapped = extractReportLookupRows<ApiDivisionRow>(res.data)
    .map(mapDivisionRow)
    .filter((row): row is UserPerformanceReportDivision => row !== null)
    .sort((a, b) => a.name.localeCompare(b.name));

  return { success: true, data: mapped };
}

function mapSearchRow(row: ApiSearchRow, index: number): UserPerformanceRow {
  const id = (row.user_id ?? row.id ?? `row-${index}`).trim();
  const name = (row.user_name ?? row.display_name ?? '').trim();
  const role = toLabel(row.user_roles ?? row.role_names ?? row.roles);
  const division = (row.division_name ?? row.division ?? '').trim();
  return {
    id,
    name,
    role,
    division,
    assigned: toCount(row.assigned ?? row.assigned_count ?? row.allocated ?? row.allocated_count),
    escalated: toCount(row.escalated ?? row.escalated_count),
    completed: toCount(row.completed ?? row.completed_count),
  };
}

function buildSearchBody(filters: UserPerformanceReportFilters): Record<string, unknown> {
  return withReportDateRange(
    {
      role_ids: nullIfEmpty(filters.roleIds),
      user_ids: nullIfEmpty(filters.userIds),
      division_ids: nullIfEmpty(filters.divisionIds),
      file_category_ids: nullIfEmpty(filters.categoryIds),
      sub_file_category_ids: nullIfEmpty(filters.subCategoryIds),
    },
    filters.dateFrom,
    filters.dateTo,
  );
}

function buildExportBody(filters: UserPerformanceReportFilters): Record<string, unknown> {
  return withReportTimeZone(
    withReportDateRange(
      {
        role_ids: nullIfEmpty(filters.roleIds),
        user_ids: nullIfEmpty(filters.userIds),
        category_ids: nullIfEmpty(filters.categoryIds),
        sub_category_ids: nullIfEmpty(filters.subCategoryIds),
        division_ids: nullIfEmpty(filters.divisionIds),
      },
      filters.dateFrom,
      filters.dateTo,
      { from: 'date_from', to: 'date_to' },
    ),
  );
}

export async function searchUserPerformanceReport(
  filters: UserPerformanceReportFilters,
  page: number,
  size: number,
): Promise<ApiResult<UserPerformanceSearchResult>> {
  const res = await reportsApiRequest<{ content?: ApiSearchRow[]; total_elements?: number; total_pages?: number; page?: number; size?: number }>(
    `/user-performance/search?page=${page}&size=${size}`,
    { method: 'POST', body: buildSearchBody(filters) },
  );
  if (!res.ok) return { success: false, message: res.message };

  const mapped = mapReportSearchPage(
    {
      ...res.data,
      content: (res.data?.content ?? []).map(mapSearchRow),
    },
    page,
    size,
  );
  return { success: true, data: mapped };
}

export async function exportUserPerformanceReport(
  format: UserPerformanceExportFormat,
  filters: UserPerformanceReportFilters,
): Promise<ApiResult<ReportExportFile>> {
  return exportReportBlob(
    `/user-performance/export?format=${encodeURIComponent(format)}`,
    buildExportBody(filters),
  );
}

export { downloadReportBlob };
